<script setup lang="ts">
import { computed } from "vue";
import Icon from "@/design/primitives/Icon.vue";
import BenOrb from "@/shared/agent-display/BenOrb.vue";
import type { AgentMode } from "../api/use-agents";
import { DEFAULT_IN_CONVERSATION, DEFAULT_OPENERS } from "../composables/default-suggestions";
import { useScreenSuggestions } from "../composables/use-screen-suggestions";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * O QUE PERGUNTAR AGORA — e é a mesma peça nos dois momentos da conversa.
 *
 * ── ANTES DA PRIMEIRA FALA: a tela vazia da V2 ────────────────────────────
 *
 * Rosto, saudação, o NOME DA TELA em que a pessoa está e uma lista VERTICAL de
 * perguntas com seta. Não é enfeite: a lista horizontal de pílulas centradas que
 * estava aqui só cabia com três frases curtas, e as sugestões boas — as que
 * dizem o que fazer neste negócio, nesta tela — não são curtas. A V2 aprendeu
 * isso e desenha uma linha por pergunta (`BenEmptyState.vue`).
 *
 * ── AS PERGUNTAS VÊM DA TELA, E DO NEGÓCIO ────────────────────────────────
 *
 * Este componente tinha três frases fixas por MODO, escritas em i18n — as mesmas
 * na caixa de entrada e no funil, iguais para um petshop e para uma clínica.
 * Agora o catálogo é por CAMINHO, escrito a partir do negócio do workspace e
 * editável em `/suggestions` (migração 0124).
 *
 * As frases por modo viraram o CHÃO: um workspace que nunca gerou o catálogo —
 * ou uma tela que a geração não cobriu — ainda abre com algo sensato para
 * perguntar, em sete idiomas. É o que a V2 resolve instalando um catálogo
 * genérico em português no banco; aqui o chão já estava escrito e traduzido, e
 * não precisa de linha nenhuma.
 *
 * Esse chão mudou-se para `composables/default-suggestions.ts` em 24/09/2026:
 * a tela de sugestões precisa de MOSTRAR o que este componente DESENHA, e uma
 * constante privada aqui fazia de "o que o Ben oferece hoje?" uma pergunta que
 * só se responde lendo código.
 *
 * ── DEPOIS DA PRIMEIRA RESPOSTA ───────────────────────────────────────────
 *
 * Os follow-ups que o próprio turno escreveu (`next`, do quadro `done`), como
 * pílulas debaixo da última fala — a primeira em destaque, como na V2
 * (`BenSuggestionChips.vue#chip--primary`). Ancorados no que o agente ACABOU de
 * fazer, porque quem os escreveu foi o turno que fez.
 */
const props = defineProps<{
	mode: AgentMode;
	/** O rosto do agente desta conversa, quando ele tem um. */
	avatarUrl?: string | null;
	/** A conversa está ANCORADA numa conversa de cliente, e não é o painel por si. */
	inConversation?: boolean;
	/** Já houve conversa: daqui em diante quem sugere é o turno. */
	started?: boolean;
	/** `next` do quadro `done`. Vazio até o servidor mandar algum. */
	next?: readonly string[];
}>();

const emit = defineEmits<{ use: [string] }>();

const { t } = useAgentsI18n();
const { items: fromScreen, screenLabel } = useScreenSuggestions();

/** A frase que abre a conversa: o que se pode pedir a ESTE agente, em prosa. */
const hint = computed(() => {
	if (props.inConversation) return t("suggestions.hintInConversation");
	return t("suggestions.hintInternal");
});

/**
 * O CABEÇALHO da lista: o nome da tela, ou a frase geral.
 *
 * A diferença entre os dois é informação real — "estas são as da Caixa de
 * entrada" e "estas são as gerais" não são a mesma promessa. O nome vem do menu
 * (`DESTINATIONS`), nunca de uma coluna guardada junto da pergunta.
 */
const category = computed(() => screenLabel.value ?? t("suggestions.toStart"));

/**
 * Uma pergunta pronta: o TEXTO que se envia e o RÓTULO que se lê.
 *
 * Os dois separados porque o catálogo permite um botão curto para uma pergunta
 * longa (`label`), e porque o chão por modo não tem rótulo nenhum — é a mesma
 * frase nos dois papéis.
 */
interface Offer {
	readonly key: string;
	readonly label: string;
	readonly question: string;
}

const offers = computed<readonly Offer[]>(() => {
	// A thread da porta da conversa é sobre uma CONVERSA e não sobre uma tela: o
	// catálogo por caminho não fala dela, e responderia com as sugestões da caixa
	// de entrada.
	if (!props.inConversation && fromScreen.value.length > 0) {
		return fromScreen.value.map((s) => ({
			key: s.id,
			label: s.label ?? s.question,
			question: s.question,
		}));
	}
	const keys = props.inConversation
		? DEFAULT_IN_CONVERSATION
		: (DEFAULT_OPENERS[props.mode === "interaction" ? "interaction" : "internal"] ?? []);
	return keys.map((key) => ({ key, label: t(key), question: t(key) }));
});
</script>

<template>
	<!-- ── DEPOIS DA PRIMEIRA FALA: as pílulas do turno ─────────────────────── -->
	<div v-if="started" class="flex flex-wrap gap-1.5">
		<button
			v-for="(suggestion, index) in next ?? []"
			:key="suggestion"
			type="button"
			class="rounded-full border px-3 py-1 text-xs transition-colors"
			:class="
				index === 0
					? 'border-transparent bg-accent-subtle text-accent hover:bg-accent hover:text-fg-on-accent'
					: 'border-border text-fg-muted hover:bg-bg-muted'
			"
			@click="emit('use', suggestion)"
		>
			{{ suggestion }}
		</button>
	</div>

	<!-- ── A CONVERSA VAZIA: o rosto, a tela e a lista ──────────────────────── -->
	<div v-else class="space-y-4 px-1 pt-5">
		<div class="flex items-center gap-3">
			<img
				v-if="avatarUrl"
				:src="avatarUrl"
				alt=""
				class="size-12 shrink-0 rounded-full object-cover"
			/>
			<BenOrb v-else state="idle" :size="48" :tone="inConversation ? 'copilot' : 'ben'" />
			<div class="min-w-0">
				<p class="text-sm font-semibold text-fg">{{ t("suggestions.hello") }}</p>
				<p class="text-xs text-fg-muted">{{ hint }}</p>
			</div>
		</div>

		<div v-if="offers.length" class="space-y-2">
			<p
				class="flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-wide text-accent"
			>
				<Icon name="sparkle" class="size-3.5" />
				<span class="min-w-0 truncate">{{ category }}</span>
			</p>

			<!-- UMA LINHA POR PERGUNTA. A seta não é enfeite: ela diz que o botão
			     ENVIA a pergunta, e não que a escreve no campo. -->
			<ul class="space-y-1.5">
				<li v-for="offer in offers" :key="offer.key">
					<button
						type="button"
						class="flex w-full items-center justify-between gap-2 rounded-lg border border-accent-border bg-accent-subtle/50 px-3 py-2.5 text-left text-xs font-medium text-fg transition-colors hover:border-accent hover:bg-accent-subtle"
						:title="offer.question"
						@click="emit('use', offer.question)"
					>
						<span class="min-w-0">{{ offer.label }}</span>
						<Icon name="arrowRight" class="size-3.5 shrink-0 text-accent" />
					</button>
				</li>
			</ul>

			<p class="text-3xs text-fg-subtle">{{ t("suggestions.orWrite") }}</p>
		</div>

		<p v-else class="text-3xs text-fg-subtle">{{ t("suggestions.orWriteAlone") }}</p>
	</div>
</template>
